import { AnimatePresence, motion } from 'framer-motion';
import React, { Fragment } from 'react';
import { Cropper } from 'react-cropper';
import { randomString } from '../../../store/utils';

interface Props {
    uploadHeaderImage: (file: File) => Promise<void>;
    uploadFooterImage: (file: File) => Promise<void>;
}

interface State {
    headerImage: string;
    footerImage: string;
    rxImage: string;
    isSaving: boolean;
}
class LetterHeadCrop extends React.PureComponent<Props, State> {
    arrayOfInputRefs: React.RefObject<any>;

    constructor(props: Props) {
        super(props);
        this.state = {
            headerImage: '',
            footerImage: '',
            rxImage: '',
            isSaving: false,
        };
        this.arrayOfInputRefs = React.createRef();
    }

    inputId = `letter_head_${randomString(4)}`;

    getCropBoxData = () => {
        const imageElement = this.arrayOfInputRefs.current;
        return imageElement?.cropper?.getCroppedCanvas().toDataURL();
    };

    takeHeaderImage = async () => {
        const data = this.getCropBoxData();
        this.setState({ headerImage: data });
    };

    takeFooterImage = async () => {
        const data = this.getCropBoxData();
        this.setState({ footerImage: data });
    };

    onUploadClick = () => {
        document.getElementById(this.inputId)?.click();
    };

    onImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const iconImage = URL.createObjectURL(e.target.files[0] as File);
            this.setState({ rxImage: iconImage });
        }
        e.target.value = '';
    };

    onSave = async () => {
        this.setState({ isSaving: true });
        try {
            const { headerImage } = this.state;
            const { uploadHeaderImage } = this.props;

            if (headerImage) {
                const base64Response = await fetch(headerImage);
                const blob = await base64Response.blob();

                const file = new File([blob], 'header-footer', {
                    type: 'image/png',
                });

                uploadHeaderImage(file);
            }

            const { footerImage } = this.state;
            const { uploadFooterImage } = this.props;

            if (footerImage) {
                const base64Response = await fetch(footerImage);
                const blob = await base64Response.blob();

                const file = new File([blob], 'header-footer', {
                    type: 'image/png',
                });

                uploadFooterImage(file);
            }

            this.setState({ rxImage: '' });
        } catch (error) {
            console.error(error);
            this.setState({ rxImage: '' });
        }
        this.setState({ isSaving: false });
    };

    handleInitialize = () => {
        if (this.arrayOfInputRefs.current) {
            const initialCropData = {
                top: 0,
                width: '100%',
            };
            this.arrayOfInputRefs.current?.cropper.setCropBoxData(initialCropData);
        }
    };

    clearHeaderImage = () => {
        this.setState({ headerImage: '' });
    };

    clearFooterImage = () => {
        this.setState({ footerImage: '' });
    };

    render(): React.ReactNode {
        const { rxImage, headerImage, footerImage, isSaving } = this.state;
        return (
            <Fragment>
                <div
                    onClick={this.onUploadClick}
                    className="flex border border-darwin-neutral-200 rounded-4 p-4 bg-white  items-center cursor-pointer justify-between px-16"
                >
                    <div>
                        <p className="font-700 ">Upload Letterhead</p>
                        <p className="text-neutral-3 text-10">Instant Setup</p>
                    </div>
                    <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        id={this.inputId}
                        className="w-full h-full"
                        onChange={this.onImageUpload}
                        hidden
                    />

                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 576 512"
                        className="w-20 h-20 fill-darwin-accent-dark-blue-800"
                    >
                        <path d="M234.7 42.7L192 64l42.7 21.3L256 128l21.3-42.7L320 64 277.3 42.7 256 0 234.7 42.7zM384.4 192.4l-32.8-32.8L432 79.2 464.8 112l-80.4 80.4zM96 32L64 96 0 128l64 32 32 64 32-64 64-32L128 96 96 32zM416 352l-64 32 64 32 32 64 32-64 64-32-64-32-32-64-32 64zM144 512l39.6-39.6L504.4 151.6 544 112 504.4 72.4 471.6 39.6 432 0 392.4 39.6 71.6 360.4 32 400l39.6 39.6 32.8 32.8L144 512z" />
                    </svg>
                </div>
                {rxImage && (
                    <AnimatePresence>
                        <motion.div
                            initial={{ opacity: 0 }}
                            exit={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            style={{ zIndex: 20 }}
                            transition={{ duration: 0.2, ease: 'linear' }}
                            className="flex absolute inset-0"
                        >
                            <div
                                className="w-full h-full bg-white px-16 pb-16 modal-content"
                                style={{
                                    borderRadius: '0px',
                                }}
                            >
                                <div className="flex font-700 text-darwin-neutral-900 text-16 justify-end border-b my-8 pb-4 pr-8 border-darwin-neutral-400">
                                    Crop Header and footer
                                </div>

                                <div className={`flex items-center w-full h-full`}>
                                    <div className="w-60p h-full pb-36">
                                        <Cropper
                                            ref={this.arrayOfInputRefs}
                                            crossOrigin="anonymous"
                                            style={{ height: '100%', width: '100%' }}
                                            zoomTo={0.5}
                                            initialAspectRatio={6}
                                            preview=".img-preview"
                                            src={rxImage}
                                            viewMode={1}
                                            minCropBoxHeight={10}
                                            minCropBoxWidth={10}
                                            background={true}
                                            responsive={true}
                                            autoCropArea={1}
                                            checkOrientation={true}
                                            guides={true}
                                            ready={this.handleInitialize}
                                        />
                                    </div>
                                    <div className="w-40p flex flex-col justify-between h-full">
                                        <div className="px-16 w-full h-full overflow-auto space-y-32 hide-scrollbar">
                                            <div className="w-full space-y-4">
                                                <h1 className="font-700 pl-4 text-darwin-neutral-900">
                                                    Header Image:
                                                </h1>
                                                <div className="relative object-fit-contain bg-darwin-neutral-50 h-240 border rounded-8 border-darwin-neutral-100">
                                                    {!headerImage && (
                                                        <span
                                                            onClick={this.takeHeaderImage}
                                                            className="z-50 h-full w-full cursor-pointer flex justify-center items-center absolute"
                                                        >
                                                            <span className="border p-8 rounded-8 font-700 text-darwin-accent-bright-blue-800">
                                                                Select as Header
                                                            </span>
                                                        </span>
                                                    )}

                                                    {headerImage && (
                                                        <div
                                                            onClick={this.clearHeaderImage}
                                                            className="absolute -right-12 -top-12 p-6 rounded-full bg-darwin-neutral-100 shadow-elevation-900 cursor-pointer"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 320 512"
                                                                fill="currentColor"
                                                                className="w-16 h-16 text-darwin-neutral-1000"
                                                            >
                                                                <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {headerImage && (
                                                        <img
                                                            src={headerImage}
                                                            className="object-contain rounded-8"
                                                            style={{
                                                                width: '100%',
                                                                height: '238px',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="w-full space-y-4">
                                                <h1 className="font-700 pl-4 text-darwin-neutral-900">
                                                    Footer Image:
                                                </h1>
                                                <div className="relative object-fit-contain bg-darwin-neutral-50 h-240 border rounded-8 border-darwin-neutral-100">
                                                    {!footerImage && (
                                                        <span
                                                            onClick={this.takeFooterImage}
                                                            className="z-50 h-full w-full cursor-pointer flex justify-center items-center absolute"
                                                        >
                                                            <span className="border p-8 rounded-8 font-700 text-darwin-accent-bright-blue-800">
                                                                Select as Footer
                                                            </span>
                                                        </span>
                                                    )}
                                                    {footerImage && (
                                                        <div
                                                            onClick={this.clearFooterImage}
                                                            className="absolute -right-12 -top-12 p-6 rounded-full bg-darwin-neutral-100 shadow-elevation-900 cursor-pointer"
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 320 512"
                                                                fill="currentColor"
                                                                className="w-16 h-16 text-darwin-neutral-1000"
                                                            >
                                                                <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {footerImage && (
                                                        <img
                                                            src={footerImage}
                                                            className="object-contain rounded-8"
                                                            style={{
                                                                width: '100%',
                                                                height: '238px',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex pt-8 pb-36 pl-64 space-x-16 items-center justify-end">
                                            <button
                                                onClick={() => {
                                                    this.setState({
                                                        rxImage: '',
                                                        headerImage: '',
                                                        footerImage: '',
                                                    });
                                                }}
                                                className="w-full  max-w-160 py-10 secondary-btn text-14"
                                            >
                                                <span className="w-full text-center">Close</span>
                                            </button>
                                            <button
                                                onClick={this.onSave}
                                                className="w-full max-w-160 py-10 px-16 primary-btn text-14"
                                            >
                                                {isSaving && (
                                                    <svg
                                                        className="w-20 fill-white animate-spin"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                                    </svg>
                                                )}
                                                <span className="w-full text-center">Save</span>

                                                <svg
                                                    className="w-16 h-16 fill-white"
                                                    role="img"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 448 512"
                                                >
                                                    <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                )}
            </Fragment>
        );
    }
}

export default LetterHeadCrop;
